import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import { faCircleDollar } from '@fortawesome/pro-regular-svg-icons/faCircleDollar';
import { faCircleSterling } from '@fortawesome/pro-regular-svg-icons/faCircleSterling';
import { faClipboardListCheck } from '@fortawesome/pro-regular-svg-icons/faClipboardListCheck';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faReceipt } from '@fortawesome/pro-regular-svg-icons/faReceipt';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faWrench } from '@fortawesome/pro-regular-svg-icons/faWrench';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Row, theme, Tooltip } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import { clientConfig } from 'pos-client/clientConfig.ts';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { useJobSlotStartAndEndDisplayTimes } from 'pos-client/components/scheduleBoard/hooks/useJobSlotStartAndEndDisplayTimes.ts';
import { useJobStatusBgColor } from 'pos-client/components/scheduleBoard/hooks/useJobStatusColourMapper.ts';
import { useJobStatusTextColor } from 'pos-client/components/scheduleBoard/hooks/useJobStatusTextColor.ts';
import { useOrderJobFulfilmentTechnicians } from 'pos-client/components/scheduleBoard/hooks/useOrderJobFulfilmentTechnicians.ts';
import type { JobDetailsModalActiveKey } from 'pos-client/components/scheduleBoard/types/boardTypes.ts';
import { useOrderJob } from 'pos-client/hooks/useOrderJob.ts';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { scheduleBoardJobPartStatus } from 'pos-client/utils/order/jobBuilderUtils.ts';
import type { ScheduleBoardJobModel } from 'pos-shared/models/schedulingSchema.ts';

import { JobSlotContentStatusIcons } from './JobSlotContentStatusIcons.tsx';
import { JobSlotDetail } from './JobSlotDetail.tsx';
import { OrderJobFulfilmentTechniciansList } from './OrderJobFulfilmentTechniciansList.tsx';
import { OrderVehicleDetailsList } from './OrderVehicleDetailsList.tsx';

interface JobSlotTooltipProps {
  appointmentData: ScheduleBoardJobModel;
  children: ReactNode | ReactNode[];
  placement?: TooltipPlacement;
  isInConflict?: boolean;
  userIsDragging: boolean;
}

export const JobSlotTooltip = ({
  appointmentData,
  children,
  placement = 'top',
  isInConflict,
  userIsDragging,
}: JobSlotTooltipProps) => {
  const { state } = appointmentData;
  const { token } = theme.useToken();
  const priceIncludesTax = clientConfig.pricesIncTax;
  const { currency } = clientConfig;
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [userHasSeenTooltip, setUserHasSeenTooltip] = useState<boolean>(false);
  const rescheduleJobPermission = hasPermissions('reschedule.job');
  const jobIsComplete = state === 'complete' || state === 'completed';
  const jobCanBeRescheduled = state !== 'cancelled';

  const { setModalActiveKey, setJobDetailsModalData } =
    useScheduleBoardContext();

  const {
    orderJobFulfilmentTechnicians,
    orderJobFulfilmentTechniciansIsLoading,
  } = useOrderJobFulfilmentTechnicians(appointmentData, userHasSeenTooltip);

  const { orderJob } = useOrderJob({
    orderId: appointmentData.orderId,
    jobId: appointmentData.jobId,
  });

  const textColor = useJobStatusTextColor(
    isInConflict ? 'conflict' : appointmentData.state
  );

  const bgColor = useJobStatusBgColor(
    isInConflict ? 'conflict' : appointmentData.state
  );

  const { startDisplay, endDisplay } = useJobSlotStartAndEndDisplayTimes(
    appointmentData.startDateDisplay,
    appointmentData.endDateDisplay,
    true
  );

  const totalCostOfJobs = String(
    orderJob?.items.reduce(
      (acc, { price }) =>
        acc + (priceIncludesTax ? (price?.incTax ?? 0) : (price?.exTax ?? 0)),
      0
    )
  );

  const handleVisibleChange = (visible: boolean) => {
    if (!userIsDragging) {
      setTooltipVisible(visible);

      if (visible && userHasSeenTooltip === false) {
        setUserHasSeenTooltip(true);
      }
    }
  };

  const handleCloseTooltip = () => {
    setTooltipVisible(false);
  };

  const handleOpenModal = (modalActiveKey: JobDetailsModalActiveKey) => {
    setModalActiveKey(modalActiveKey);
    handleCloseTooltip();
    setJobDetailsModalData(appointmentData);
  };

  const partsArray = appointmentData?.items ?? [];
  const hasParts = partsArray.length > 0;
  const partStatus = hasParts ? scheduleBoardJobPartStatus(partsArray) : '';

  return (
    <Tooltip
      styles={{
        root: { minWidth: 500 },
        body: { padding: token.padding },
      }}
      color={bgColor}
      placement={placement}
      open={userIsDragging ? false : tooltipVisible}
      onOpenChange={handleVisibleChange}
      className={`slot-tooltip slot-tooltip-`}
      title={
        <Row
          style={{
            fontSize: token.fontSizeSM,
            color: textColor,
            position: 'relative',
          }}
        >
          <JobSlotContentStatusIcons
            slotType={appointmentData.slotType}
            jobData={appointmentData}
          />
          <JobSlotDetail
            detail={`${appointmentData.jobType} (${appointmentData.jobReference})`}
            icon={faClipboardListCheck}
            dividerWidth="90%"
            fontWeight="strong"
          />
          <JobSlotDetail
            detail={`${appointmentData.customer?.firstName} ${appointmentData.customer?.lastName}`}
            icon={faUser}
          />
          {hasParts && (
            <JobSlotDetail
              detail={t(`statuses.${partStatus}`, {
                defaultValue: partStatus,
              })}
              icon={faWrench}
            />
          )}
          <JobSlotDetail
            detailsList={[startDisplay, endDisplay]}
            icon={faClock}
          />
          <OrderJobFulfilmentTechniciansList
            appointmentData={appointmentData}
            orderJobFulfilmentTechnicians={orderJobFulfilmentTechnicians}
            orderJobFulfilmentTechniciansIsLoading={
              orderJobFulfilmentTechniciansIsLoading
            }
            isInConflict={isInConflict}
          />
          <OrderVehicleDetailsList appointmentData={appointmentData} />

          {appointmentData.bookingReference && (
            <JobSlotDetail
              detail={appointmentData.bookingReference}
              icon={faReceipt}
            />
          )}
          {orderJob && (
            <JobSlotDetail
              detail={totalCostOfJobs}
              icon={currency === 'GBP' ? faCircleSterling : faCircleDollar}
            />
          )}
          <Row
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: token.marginSM,
            }}
          >
            <Button
              style={{ color: textColor, borderColor: textColor }}
              onClick={() => handleOpenModal('1')}
              icon={<FontAwesomeIcon icon={faClipboardListCheck} />}
            >
              {t('jobs.jobDetails')}
            </Button>
            {rescheduleJobPermission && jobCanBeRescheduled && (
              <Link
                to={`/orders/manage-orders/${appointmentData?.orderId}/reschedule/${appointmentData?.bookingReference}/${appointmentData?.scheduleId}`}
                style={{ marginLeft: token.marginSM }}
              >
                <Button
                  style={{ color: textColor, borderColor: textColor }}
                  icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                  disabled={jobIsComplete}
                >
                  {t('scheduling.reschedule')}
                </Button>
              </Link>
            )}
          </Row>
        </Row>
      }
    >
      {children}
    </Tooltip>
  );
};
